import { GoogleMap } from "@react-google-maps/api";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import LocationFormField from "@/components/Util/FormField/LocationFormField";
import { AnalyticsActions, AnalyticsCategories, AnalyticsEventType, AnalyticsLabels } from "@/constants/analytics";
import { SuperCluster } from "@daubit/points-cluster";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "@/app/i18n/client";
import { BusRequest } from "@/types/busrequest";
import { Company } from "@/types/company";
import { AddressWithLocation, AdvertiserLocation, GeoLocation } from "@/types/location";
import { Cluster, CMarker } from "@/types/map";
import { advertiserLocationClusterIcon, calcBusrequestClusterIcon, advertiserDafaultIcon } from "./ClusterIcons";
import MarkerV2 from "./MarkerV2";
import { sendGTMEvent } from "@/components/Tracking/TagManagerWrapper";
import { MdFullscreen, MdFullscreenExit } from "react-icons/md";
import { useGoogleMaps } from "@/utils/google-maps-context";
const defaultProps = {
  center: {
    lat: 51.218043,
    lng: 10.350282
  },
  zoom: 6,
  radius: 40,
  minZoom: 4,
  maxZoom: 16
};
export const MarkerType = {
  BusRequest: "busrequest",
  Advertiser: "advertiser",
  Companies: "companies"
} as const;
export type MarkerType = (typeof MarkerType)[keyof typeof MarkerType];
interface MapProps {
  center?: google.maps.LatLng;
  zoom?: number;
  bounds?: google.maps.LatLngBounds;
}
function isLocation(location: GeoLocation | undefined): boolean {
  return location ? location.latitude !== 0 && location.longitude !== 0 : false;
}

// Default to looking at germany

const defaultBounds = {
  north: 55.16394374013646,
  south: 46.902404581147465,
  east: 31.44403200000002,
  west: -10.743467999999979
} as google.maps.LatLngBoundsLiteral;
const defaultZoom = 6;
const defaultCenter = {
  lat: 51.218042999999994,
  lng: 10.350282000000021
} as google.maps.LatLngLiteral;

/**
 * Must have google maps library already loaded. Check with useGoogleMaps hook and only render this Map when it is.
 * @param param0
 * @returns
 */

export default function Map({
  showClusterModal,
  showSingleMarkerModal,
  visibleItems,
  type,
  height,
  hideSearchField,
  searchFieldClassName,
  className
}: {
  showClusterModal: (ids: string[]) => void;
  showSingleMarkerModal?: (id: string) => void;
  visibleItems: (BusRequest | AdvertiserLocation | Company)[];
  type: MarkerType;
  height: string;
  hideSearchField?: boolean;
  searchFieldClassName?: string;
  className?: string;
}) {
  const {
    t
  } = useTranslation();
  const zoomControls = useRef<HTMLDivElement | null>(null);
  const fullScreenControls = useRef<HTMLDivElement | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [map, setMap] = useState<google.maps.Map>();
  const [zoom, setZoom] = useState<number>(defaultZoom);
  //BUG: google.maps.LatLng constructor not found at runtime
  const [center, setCenter] = useState<google.maps.LatLng>(new google.maps.LatLng(defaultCenter));
  const [bounds, setBounds] = useState<google.maps.LatLngBounds>(new google.maps.LatLngBounds(defaultBounds));

  /**
   * Handles a Click on a cluster.
   * Opens a modal with the given requests of the marker.
   * @param markers
   */
  function handleClickClusterer(cluster: Cluster): void {
    const ids = cluster.items.map(c => c.id);
    if (showSingleMarkerModal) {
      if (ids.length === 1) {
        showSingleMarkerModal(ids[0]);
      } else {
        showClusterModal(ids);
      }
    } else {
      showClusterModal(ids);
    }
    sendGTMEvent(AnalyticsEventType.Event, AnalyticsCategories.map, AnalyticsLabels.clusterMarker, AnalyticsActions.openedClusterModal, {
      value: String(ids.length)
    });
  }
  function handleMapMove(): void {
    const center_0 = map?.getCenter();
    if (center_0) {
      setCenter(center_0);
    }
    const bounds_0 = map?.getBounds();
    if (bounds_0) {
      setBounds(bounds_0);
    }
    const zoom_0 = map?.getZoom();
    if (zoom_0) {
      setZoom(zoom_0);
    }
  }
  const clusterer = useMemo(() => {
    let items: CMarker[] = [];
    if (type === MarkerType.BusRequest) {
      const busRequests = visibleItems as BusRequest[];
      items = busRequests.flatMap(marker => {
        const m1 = {
          id: "start_" + marker.id,
          lat: marker.start.address.location.latitude,
          lng: marker.start.address.location.longitude,
          item: marker
        };
        let m2 = undefined;
        if (marker.end && isLocation(marker.end.address.location)) {
          m2 = {
            id: "end_" + marker.id,
            lat: marker.end.address.location.latitude,
            lng: marker.end.address.location.longitude,
            item: marker
          };
        }
        if (m2) {
          return [m1, m2];
        } else {
          return [m1];
        }
      });
    } else if (type === MarkerType.Companies) {
      const companies = visibleItems as Company<"busoperator" | "touroperator">[];
      items = companies.map(marker_0 => {
        return {
          id: "location_" + marker_0.id,
          lat: marker_0.contactData.address.location.latitude,
          lng: marker_0.contactData.address.location.longitude,
          item: marker_0
        };
      });
    } else if (type === MarkerType.Advertiser) {
      const advertiserLocations = visibleItems as AdvertiserLocation[];
      items = advertiserLocations.map(marker_1 => {
        return {
          id: "location_" + marker_1.id,
          lat: marker_1.address.location.latitude,
          lng: marker_1.address.location.longitude,
          item: marker_1
        };
      });
    }
    const instance = new SuperCluster<CMarker>({
      minZoom: 0,
      maxZoom: 16,
      radius: 40
    });
    instance.load(items);
    return instance;
  }, [type, visibleItems]);
  const getClusters = useCallback((props: MapProps) => {
    if (props.zoom && props.bounds) {
      const ne = props.bounds.getNorthEast();
      const sw = props.bounds.getSouthWest();
      console.log({
        minX: sw.lng(),
        minY: sw.lat(),
        maxX: ne.lng(),
        maxY: ne.lat()
      });
      const newClusters = clusterer.getClusters({
        minX: sw.lng(),
        minY: sw.lat(),
        maxX: ne.lng(),
        maxY: ne.lat()
      }, props.zoom).map(sCluster => {
        const seen = new Set();
        const dedupedItems = sCluster.points.map((point: CMarker) => point.item).filter(item => {
          if (seen.has(item.id)) return false;
          seen.add(item.id);
          return true;
        });
        return {
          lat: sCluster.wy,
          lng: sCluster.wx,
          count: sCluster.numPoints,
          id: `${sCluster.numPoints}_${sCluster.points[0].id}`,
          items: dedupedItems
        };
      });
      return newClusters;
    }
    return [];
  }, [clusterer]);
  const clusters = useMemo(() => {
    if (center && zoom && bounds) {
      return getClusters({
        center,
        zoom,
        bounds
      });
    }
    return [];
  }, [bounds, center, getClusters, zoom]);
  const handleApiLoaded = (map_0: google.maps.Map): void => {
    initZoomControl(map_0);
    initFullscreenControl(map_0);
    setMap(map_0);
    map_0.setCenter(center);
    map_0.setZoom(zoom);
  };

  /**
   * Sets the position of the map to the new location given by the autocomplete component
   */
  function updateMapLocation(address: AddressWithLocation): void {
    const newCenter = new google.maps.LatLng({
      lat: address.location.latitude,
      lng: address.location.longitude
    });
    map?.setCenter(newCenter);
    map?.setZoom(9);
    setZoom(9);
    setCenter(newCenter);
  }
  function resetMapLocation(): void {
    map?.setCenter(defaultCenter);
    map?.setZoom(defaultZoom);
  }
  function initZoomControl(map_1: google.maps.Map): void {
    map_1.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(zoomControls.current as HTMLDivElement);
  }
  function initFullscreenControl(map_2: google.maps.Map): void {
    const elementToSendFullscreen = map_2.getDiv().firstChild as HTMLElement;
    const fullscreenControl = fullScreenControls.current as HTMLDivElement;
    map_2.controls[google.maps.ControlPosition.RIGHT_TOP].push(fullscreenControl);
    fullscreenControl.onclick = function (): void {
      if (isFullscreen(elementToSendFullscreen)) {
        exitFullscreen();
      } else {
        requestFullscreen(elementToSendFullscreen);
      }
    };
    document.onfullscreenchange = function (): void {
      if (isFullscreen(elementToSendFullscreen)) {
        setIsFullScreen(true);
      } else {
        setIsFullScreen(false);
      }
    };
  }
  function isFullscreen(element: HTMLElement): boolean {
    return document.fullscreenElement === element;
  }
  function requestFullscreen(element_0: HTMLElement): void {
    if (element_0.requestFullscreen) {
      void element_0.requestFullscreen();
    }
  }
  function exitFullscreen(): void {
    if (document.exitFullscreen) {
      void document.exitFullscreen();
    }
  }
  const {
    isLoaded
  } = useGoogleMaps();
  const markerLabel = (count: number) => {
    return {
      label: {
        text: count.toString(),
        fontSize: "18px",
        fontWeight: "700"
      }
    };
  };
  return <div className={`google-map-container ${className}`}>
      {isLoaded ? <>
          {!hideSearchField && <div className="map-location-input">
              <LocationFormField name="mapLocation" formType="change" placeholder={t("MAIN.SEARCHLOCATION.TEXT")} design="old" setLocation={updateMapLocation} onClear={resetMapLocation} className={searchFieldClassName} />
            </div>}
          <GoogleMap mapContainerStyle={{
        height: height,
        width: "100%"
      }} options={{
        disableDefaultUI: true,
        styles: undefined
      }} onLoad={(map_3): void => {
        handleApiLoaded(map_3);
      }} clickableIcons={true} onCenterChanged={handleMapMove} onBoundsChanged={handleMapMove} onZoomChanged={handleMapMove}>
            {clusters.map(c_0 => type === MarkerType.BusRequest ? <MarkerV2 key={c_0.id} position={{
          lat: c_0.lat,
          lng: c_0.lng
        }} onClick={() => handleClickClusterer(c_0)} options={c_0.items.length > 1 ? markerLabel(c_0.items.length) : undefined} icon={calcBusrequestClusterIcon(c_0.items.map(i => (i as BusRequest).category))} /> : <MarkerV2 key={c_0.id} position={{
          lat: c_0.lat,
          lng: c_0.lng
        }} onClick={() => handleClickClusterer(c_0)} options={c_0.count === 1 ? undefined : markerLabel(c_0.count)} icon={c_0.count === 1 ? advertiserDafaultIcon : advertiserLocationClusterIcon} />)}
          </GoogleMap>
          <div className="hidden">
            <div className="controls zoom-control" ref={zoomControls}>
              <button className="zoom-control-in" title="Zoom In" onClick={(): void => {
            setZoom(old => old === defaultProps.maxZoom ? old : old + 1);
            map?.setZoom(zoom === defaultProps.minZoom ? zoom : zoom + 1);
            handleMapMove();
          }}>
                +
              </button>
              <button className="zoom-control-out" title="Zoom Out" onClick={(): void => {
            setZoom(old_0 => old_0 === defaultProps.minZoom ? old_0 : old_0 - 1);
            map?.setZoom(zoom === defaultProps.minZoom ? zoom : zoom - 1);
            handleMapMove();
          }}>
                −
              </button>
            </div>
            <div className={"controls fullscreen-control"} ref={fullScreenControls}>
              <button title="Toggle Fullscreen">
                <div className="icon">
                  {isFullScreen ? <MdFullscreenExit /> : <MdFullscreen />}
                </div>
              </button>
            </div>
          </div>
        </> : <LoadingSpinner embedded={true} label={t("LOADING.LABEL.MAP") ?? "Map"} />}
    </div>;
}